// @flow
import React from "react";
import Typography from "elevate-ui/Typography";
import Hr from "elevate-ui/Hr";
import withStyles from "elevate-ui/withStyles";

type $Props = {
  classes: Object,
};

const AccessibilityBody = ({ classes }: $Props) => {
  return (
    <div className={classes.root}>
      <Typography type="heading2" gutterTop gutterBottom>
        Accessibility
      </Typography>
      <Hr />
      <Typography type="body" gutterTop gutterBottom>
        Elm Street Technology, LLC (Elm Street) is committed to providing an
        accessible website. If you have difficulty accessing content, have
        difficulty viewing a file on the website, or notice any accessibility
        problems, please{" "}
        <a href="mailto:support@elmstreettechnology.com">contact Elm Street</a>{" "}
        to specify the nature of the accessibility issue and any assistive
        technology you use. Elm Street Technology will strive to provide the
        content you need in the format you require.
      </Typography>
      <Typography type="body" gutterTop gutterBottom>
        Elm Street welcomes your suggestions and comments about improving
        ongoing efforts to increase the accessibility of this website.
      </Typography>
      <Typography type="heading5" gutterTop gutterBottom>
        Web Accessibility Help
      </Typography>
      <Typography type="body" gutterTop gutterBottom>
        There are actions you can take to adjust your web browser to make your
        web experience more accessible.
      </Typography>
      <Typography type="heading5" gutterTop gutterBottom>
        I am blind or can't see very well
      </Typography>
      <Typography type="body" gutterTop gutterBottom>
        If you have trouble seeing web pages,{" "}
        <a
          href="https://www.ssa.gov/accessibility/"
          target="_blank"
          rel="noopener noreferrer"
        >
          the US Social Security Administration offers these tips
        </a>{" "}
        for optimizing your computer and browser to improve your online
        experience.
      </Typography>
      <ul>
        <li>
          <a
            href="https://www.w3.org/WAI/users/browsing#tts"
            target="_blank"
            rel="noopener noreferrer"
          >
            Use a text-to-speech program to read web pages out loud
          </a>
        </li>
        <li>
          <a
            href="https://www.w3.org/WAI/users/browsing#keyboard"
            target="_blank"
            rel="noopener noreferrer"
          >
            Use the keyboard to navigate screens
          </a>
        </li>
        <li>
          <a
            href="https://www.w3.org/WAI/users/browsing#text"
            target="_blank"
            rel="noopener noreferrer"
          >
            Increase text size
          </a>
        </li>
        <li>
          <a
            href="https://www.w3.org/WAI/users/browsing#text-tools"
            target="_blank"
            rel="noopener noreferrer"
          >
            Magnify your screen
          </a>
        </li>
        <li>
          <a
            href="https://www.w3.org/WAI/users/browsing#schemes"
            target="_blank"
            rel="noopener noreferrer"
          >
            Change background and text colors
          </a>
        </li>
        <li>
          <a
            href="https://www.w3.org/WAI/users/browsing#pointing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Make your mouse pointer more visible
          </a>{" "}
          (Windows only)
        </li>
      </ul>
      <Typography type="heading5" gutterTop gutterBottom>
        I find a keyboard or mouse hard to use
      </Typography>
      <Typography type="body" gutterTop gutterBottom>
        If you find a keyboard or mouse difficult to use, speech recognition
        software such as{" "}
        <a
          href="http://www.nuance.com/dragon/index.htm"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dragon NaturallySpeaking
        </a>{" "}
        may help you navigate web pages and online services. This software
        allows the user to move focus around a web page or application screen
        through voice controls.
      </Typography>
      <Typography type="heading5" gutterTop gutterBottom>
        I am deaf or hard of hearing
      </Typography>
      <Typography type="body" gutterTop gutterBottom>
        If you are deaf or hard of hearing, there are several accessibility
        features available to you.
      </Typography>
      <ul>
        <li>Transcripts</li>
        <span>
          A text transcript is a text equivalent of audio information that
          includes spoken words and non-spoken sounds such as sound effects. Elm
          Street is working on adding transcripts to all scripted video and
          audio content.
        </span>
        <li>Captioning</li>
        <span>
          A caption is transcript for the audio track of a video presentation
          that is synchronized with the video and audio tracks. Captions are
          generally rendered visually by being superimposed over the video,
          which benefits people who are deaf and hard of hearing, and anyone who
          cannot hear the audio (e.g., when in a crowded room). Most of Elm
          Street's video content includes captions.{" "}
          <a
            href="https://support.google.com/youtube/answer/100078?hl=en"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn how to turn captioning on and off in YouTube.
          </a>
        </span>
        <li>Volume controls</li>
        <span>
          Your computer, tablet, or mobile device has volume control features.
          Each video and audio service has its own additional volume controls.
          Try adjusting both your device's volume controls and your media
          players' volume controls to optimize your listening experience.
        </span>
      </ul>
    </div>
  );
};

const styles = (theme) => ({
  root: {
    position: "relative",
    width: "100%",
    maxWidth: "970px",
    margin: "40px auto",
    "& p": {
      maxWidth: "100%",
    },
    "& ul": {
      margin: "28px",
      listStyle: "circle",
      fontSize: "14px",
      "& li": {
        marginTop: theme.spacing.unit * 0.75,
        marginBottom: theme.spacing.unit * 0.75,
      },
      "& span": {
        lineHeight: "1.5em",
      },
    },
  },
});

export default withStyles(styles)(AccessibilityBody);
